import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import {
    listClients,
    listClientProjects,
    listClientRuns,
    createClient,
    PartnerClient,
    ProjectWithLatest,
} from "../../api/partner";
import { ProjectCreateModal } from "../../features/projects/components/ProjectCreateModal";
import { ProfileSourceBadge } from "../../components/Badge";
import { formatImpact, formatScientific } from "../../utils/format";
import { useAllBaselines } from "../../features/baseline/hooks/useBaseline";
import type { BaselineSummary } from "../../api/baseline";
import {
    Button,
    PageLayout,
    PageHeader,
    Section,
    PageSpinner,
    EmptyState,
} from "../../components";
import { usePortfolioOverview, usePortfolioProjects } from "../../features/portfolio-dashboard/hooks/usePortfolioSummary";
import { useTagDefinitions } from "../../features/portfolio-dashboard/hooks/usePortfolioTags";
import { PortfolioKPIBar } from "../../features/portfolio-dashboard/components/PortfolioKPIBar";
import { FleetBracket } from "../../features/portfolio-dashboard/components/FleetBracket";
import { RangeModeProvider } from "../../features/impact/context/RangeModeContext";
import { RangeModeToggle } from "../../features/impact/components/RangeModeToggle";
import { GroupBySelector } from "../../features/portfolio-dashboard/components/GroupBySelector";
import { PortfolioGroupCard } from "../../features/portfolio-dashboard/components/PortfolioGroupCard";
import { PortfolioFilterBar } from "../../features/portfolio-dashboard/components/PortfolioFilterBar";
import { PortfolioProjectList } from "../../features/portfolio-dashboard/components/PortfolioProjectList";
import { ExportButton } from "../../features/portfolio-dashboard/components/ExportButton";
import { PortfolioReportBuilderModal } from "../../features/portfolio-report/PortfolioReportBuilderModal";
import { TagManager } from "../../features/portfolio-dashboard/components/TagManager";
import { PortfolioImpactExplorer } from "../../features/portfolio-dashboard/components/PortfolioImpactExplorer";
import { PortfolioGenerationChart } from "../../features/portfolio-dashboard/components/PortfolioGenerationChart";
import { PortfolioEmissionsCard } from "../../features/portfolio-dashboard/components/PortfolioEmissionsCard";
import { PortfolioMap } from "../../features/portfolio-dashboard/components/PortfolioMap";

// ---------------------------------------------------------------------------
// Impact aggregation helper (reused from PortfolioImpact pattern)
// ---------------------------------------------------------------------------

interface ProjectImpact {
    dalys: number;
    pdfs: number;
    usd: number;
    mw: number;
    generationMwh: number;
}

function aggregateImpacts(runs: any[]): {
    total: { dalys: number; pdfs: number; usd: number };
    byProject: Record<string, ProjectImpact>;
} {
    const empty = { total: { dalys: 0, pdfs: 0, usd: 0 }, byProject: {} };
    if (!runs?.length) return empty;

    // Group by project, keep latest completed run per project
    const grouped: Record<string, any[]> = {};
    runs.forEach(run => {
        if (run.project_id && run.status === "completed") {
            (grouped[run.project_id] ??= []).push(run);
        }
    });

    let totalDalys = 0, totalPdfs = 0, totalUsd = 0;
    const byProject: Record<string, ProjectImpact> = {};

    Object.entries(grouped).forEach(([projectId, projectRuns]) => {
        // Prefer most conservative (highest confidence) profile run
        const PROFILE_PREF = ["P95", "P90", "P75", "P50"];
        const preferredRun = PROFILE_PREF.reduce<typeof projectRuns[0] | undefined>(
            (found, label) => found || projectRuns.find(r => r.profile_label === label),
            undefined,
        );
        const latest = preferredRun || [...projectRuns].sort((a, b) =>
            new Date(b.run_date || b.updated_at || 0).getTime() -
            new Date(a.run_date || a.updated_at || 0).getTime()
        )[0];
        // Cradle_to_Grave_Analysis = full cradle-to-grave (with infrastructure)
        const t = latest?.summary_json?.Impacts?.Cradle_to_Grave_Analysis?.Total;
        if (!t) return;

        const d = typeof t.DALYs === "string" ? parseFloat(t.DALYs) : t.DALYs || 0;
        const p = typeof t.PDFs === "string" ? parseFloat(t.PDFs) : t.PDFs || 0;
        const u = typeof t.USD === "string" ? parseFloat(t.USD) : t.USD || 0;

        if (!isNaN(d)) totalDalys += d;
        if (!isNaN(p)) totalPdfs += p;
        if (!isNaN(u)) totalUsd += u;

        // Extract generation MWh from generation_metadata
        const genGwh = latest?.summary_json?.generation_metadata?.actual_generation_gwh;
        const generationMwh = genGwh ? genGwh * 1000 : 0;

        byProject[projectId] = {
            dalys: isNaN(d) ? 0 : d,
            pdfs: isNaN(p) ? 0 : p,
            usd: isNaN(u) ? 0 : u,
            mw: latest.mw_capacity || 0,
            generationMwh,
        };
    });

    return {
        total: { dalys: totalDalys, pdfs: totalPdfs, usd: totalUsd },
        byProject,
    };
}

function totalMWFromRuns(runs: any[]): number {
    if (!runs?.length) return 0;
    const seen = new Set<string>();
    let total = 0;
    const sorted = [...runs].sort((a, b) =>
        new Date(b.run_date || 0).getTime() - new Date(a.run_date || 0).getTime()
    );
    sorted.forEach(run => {
        if (run.project_id && !seen.has(run.project_id)) {
            seen.add(run.project_id);
            total += run.mw_capacity || 0;
        }
    });
    return total;
}

// ---------------------------------------------------------------------------
// Main export — routes to partner or standard view
// ---------------------------------------------------------------------------

export default function Dashboard() {
    const { isPartner } = useAuth();
    return isPartner ? <PartnerDashboard /> : <CorporateDashboard />;
}

// ---------------------------------------------------------------------------
// Partner Dashboard — client cards with aggregated impacts + project rows
// ---------------------------------------------------------------------------

function PartnerDashboard() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [showCreate, setShowCreate] = useState(false);
    const [newClientName, setNewClientName] = useState("");
    const [newContactEmail, setNewContactEmail] = useState("");
    const [createError, setCreateError] = useState<string | null>(null);
    const [addProjectClientId, setAddProjectClientId] = useState<string | null>(null);

    // Fetch all baselines for grid context
    const { data: baselines } = useAllBaselines();
    const baselineMap = useMemo(() => {
        const map: Record<string, BaselineSummary> = {};
        baselines?.forEach(b => { map[b.ba_code] = b; });
        return map;
    }, [baselines]);

    // 1. Fetch clients
    const {
        data: clients,
        isLoading: clientsLoading,
        error: clientsError,
    } = useQuery<PartnerClient[]>({
        queryKey: ["partner-clients"],
        queryFn: () => listClients(token!),
        enabled: !!token,
        staleTime: 1000 * 60 * 5,
    });

    // 2. Per-client: fetch projects + model runs in parallel
    const clientDataQueries = useQueries({
        queries: (clients ?? []).map(client => ({
            queryKey: ["client-dashboard-data", client.customer_id],
            queryFn: async () => {
                const [projects, runs] = await Promise.all([
                    listClientProjects(client.customer_id, token!),
                    listClientRuns(client.customer_id, token!),
                ]);
                const runsArr = Array.isArray(runs) ? runs : [];
                const agg = aggregateImpacts(runsArr);
                return {
                    clientId: client.customer_id,
                    projects: Array.isArray(projects) ? projects : [],
                    impacts: agg.total,
                    projectImpacts: agg.byProject,
                    totalMW: totalMWFromRuns(runsArr),
                };
            },
            enabled: !!token && !!clients?.length,
            staleTime: 1000 * 60 * 5,
        })),
    });

    // Create client mutation
    const createMutation = useMutation({
        mutationFn: (data: { client_name: string; contact_email?: string }) =>
            createClient(data, token!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["partner-clients"] });
            setShowCreate(false);
            setNewClientName("");
            setNewContactEmail("");
            setCreateError(null);
        },
        onError: (err: Error) => setCreateError(err.message),
    });

    const handleCreate = () => {
        if (!newClientName.trim()) return;
        setCreateError(null);
        createMutation.mutate({
            client_name: newClientName.trim(),
            contact_email: newContactEmail.trim() || undefined,
        });
    };

    // Build lookup: client_id → { projects, impacts, projectImpacts, totalMW }
    const clientDataMap = useMemo(() => {
        const map: Record<string, {
            projects: ProjectWithLatest[];
            impacts: { dalys: number; pdfs: number; usd: number };
            projectImpacts: Record<string, ProjectImpact>;
            totalMW: number;
        }> = {};
        clientDataQueries.forEach(q => {
            if (q.data) map[q.data.clientId] = q.data;
        });
        return map;
    }, [clientDataQueries]);

    if (clientsLoading) {
        return <PageSpinner message="Loading clients..." />;
    }

    return (
        <PageLayout>
            <PageHeader
                title="Clients"
                subtitle="Manage your clients and their project impacts"
                actions={
                    <Button variant="primary" icon={<PlusIcon />} onClick={() => setShowCreate(true)}>
                        New Client
                    </Button>
                }
            />

            {/* Error banner */}
            {clientsError && (
                <div className="mb-6 p-4 rounded-lg bg-health-500/10 border border-health-500/30 flex items-center gap-3">
                    <AlertIcon className="text-health-400 flex-shrink-0" />
                    <span className="text-body-sm text-health-300">
                        Error loading clients. Please try refreshing the page.
                    </span>
                </div>
            )}

            {/* Create Client Modal */}
            {showCreate && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
                    onClick={() => setShowCreate(false)}
                >
                    <div
                        className="w-full max-w-md rounded-xl bg-surface-700 border border-border p-6 shadow-xl"
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 className="mb-4 text-h4 text-text-primary">New Client</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-body-sm text-text-secondary mb-1">Client Name *</label>
                                <input
                                    type="text"
                                    value={newClientName}
                                    onChange={e => setNewClientName(e.target.value)}
                                    className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
                                    placeholder="e.g. Acme Energy"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-body-sm text-text-secondary mb-1">Contact Email</label>
                                <input
                                    type="email"
                                    value={newContactEmail}
                                    onChange={e => setNewContactEmail(e.target.value)}
                                    className="w-full rounded-lg bg-surface-600 border border-border px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
                                    placeholder="contact@acme-energy.com"
                                />
                            </div>
                            {createError && <p className="text-health-400 text-body-sm">{createError}</p>}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={handleCreate}
                                disabled={!newClientName.trim() || createMutation.isPending}
                            >
                                {createMutation.isPending ? "Creating..." : "Create Client"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!clients?.length && (
                <Section>
                    <EmptyState
                        title="No clients yet"
                        description="Create your first client to start screening projects."
                        action={{ label: "Create Client", onClick: () => setShowCreate(true) }}
                    />
                </Section>
            )}

            {/* Client Cards */}
            <div className="space-y-6">
                {(clients ?? []).map(client => {
                    const data = clientDataMap[client.customer_id];
                    const projects = data?.projects ?? [];
                    const impacts = data?.impacts ?? { dalys: 0, pdfs: 0, usd: 0 };
                    const projectImpacts = data?.projectImpacts ?? {};
                    const mw = data?.totalMW ?? 0;
                    const isClientLoading = !data;

                    return (
                        <div
                            key={client.customer_id}
                            className="rounded-xl bg-surface-700 border border-border p-6"
                        >
                            {/* Header row */}
                            <div className="flex items-center justify-between mb-1">
                                <Link
                                    to={`/app/clients/${client.customer_id}`}
                                    className="text-lg font-semibold text-text-primary hover:text-quantum-400 transition-colors"
                                >
                                    {client.client_name}
                                </Link>
                                <span className="text-body-sm text-text-muted">
                                    {client.project_count} project{client.project_count !== 1 ? "s" : ""}
                                    {mw > 0 && ` · ${Math.round(mw)} MW`}
                                </span>
                            </div>
                            <p className="text-caption text-text-muted mb-4">Predicted Cradle to Grave Impact</p>

                            {/* Per-client loading */}
                            {isClientLoading ? (
                                <div className="flex items-center gap-2 text-text-muted text-body-sm py-4">
                                    <div className="h-4 w-4 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
                                    Loading impacts...
                                </div>
                            ) : (
                                <>
                                    {/* Impact metrics row */}
                                    <div className="grid grid-cols-3 gap-4 mb-4">
                                        <ImpactPill
                                            label="Health (DALYs)"
                                            value={formatImpact(impacts.dalys, "dalys")}
                                            colorClass={impacts.dalys < 0 ? "text-amber-400" : "text-health-400"}
                                        />
                                        <ImpactPill
                                            label="Ecosystem (PDFs)"
                                            value={formatImpact(impacts.pdfs, "pdfs")}
                                            colorClass={impacts.pdfs < 0 ? "text-amber-400" : "text-ecosystem-400"}
                                        />
                                        <ImpactPill
                                            label="Economic (USD)"
                                            value={formatImpact(impacts.usd, "usd")}
                                            colorClass={impacts.usd < 0 ? "text-amber-400" : "text-economic-400"}
                                        />
                                    </div>

                                    {/* Divider + project rows with individual impacts */}
                                    <div className="border-t border-border my-4" />
                                    {projects.length > 0 && (
                                        <>
                                            {/* Column headers */}
                                            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 items-center px-2 mb-2">
                                                <span className="text-caption text-text-muted uppercase tracking-wide">Project</span>
                                                <span className="text-caption text-text-muted uppercase tracking-wide text-right w-20">DALYs</span>
                                                <span className="text-caption text-text-muted uppercase tracking-wide text-right w-20">PDFs</span>
                                                <span className="text-caption text-text-muted uppercase tracking-wide text-right w-24">USD</span>
                                            </div>
                                            {/* Project rows */}
                                            {projects.map(proj => {
                                                const pi = projectImpacts[proj.project_id];
                                                const genMwh = pi?.generationMwh || 0;
                                                const baseline = proj.eia_ba_code ? baselineMap[proj.eia_ba_code] : undefined;
                                                return (
                                                    <Link
                                                        key={proj.project_id}
                                                        to={`/app/projects/${encodeURIComponent(proj.project_id)}?name=${encodeURIComponent(proj.project_name)}`}
                                                        className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 items-start px-2 py-2 rounded-lg hover:bg-surface-600 transition-colors"
                                                    >
                                                        <span className="flex items-center gap-2 text-body-sm text-text-primary min-w-0 pt-0.5">
                                                            <span
                                                                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                                                    proj.latest_run_status === "completed"
                                                                        ? "bg-quantum-400"
                                                                        : proj.latest_run_status === "running" || proj.latest_run_status === "queued"
                                                                        ? "bg-yellow-400"
                                                                        : "bg-text-muted"
                                                                }`}
                                                            />
                                                            <span className="truncate">{proj.project_name}</span>
                                                            {pi && <span className="text-caption text-text-muted flex-shrink-0">{Math.round(pi.mw)} MW</span>}
                                                            {proj.profile_primary_source && (
                                                                <ProfileSourceBadge
                                                                    source={proj.profile_primary_source}
                                                                    meteredPct={proj.profile_metered_pct}
                                                                    breakdown={proj.profile_source_breakdown}
                                                                    className="flex-shrink-0"
                                                                />
                                                            )}
                                                        </span>
                                                        <div className="text-right w-24">
                                                            <span className={`text-body-sm ${pi ? (pi.dalys < 0 ? "text-amber-400" : "text-health-400") : "text-text-muted"}`}>
                                                                {pi ? formatImpact(pi.dalys, "dalys") : "—"}
                                                            </span>
                                                            {pi && genMwh > 0 && (
                                                                <div className="text-[10px] text-text-muted">{formatScientific(Math.abs(pi.dalys / genMwh))} /MWh</div>
                                                            )}
                                                        </div>
                                                        <div className="text-right w-24">
                                                            <span className={`text-body-sm ${pi ? (pi.pdfs < 0 ? "text-amber-400" : "text-ecosystem-400") : "text-text-muted"}`}>
                                                                {pi ? formatImpact(pi.pdfs, "pdfs") : "—"}
                                                            </span>
                                                            {pi && genMwh > 0 && (
                                                                <div className="text-[10px] text-text-muted">{formatScientific(Math.abs(pi.pdfs / genMwh))} /MWh</div>
                                                            )}
                                                        </div>
                                                        <div className="text-right w-28">
                                                            <span className={`text-body-sm ${pi ? (pi.usd < 0 ? "text-amber-400" : "text-economic-400") : "text-text-muted"}`}>
                                                                {pi ? formatImpact(pi.usd, "usd") : "—"}
                                                            </span>
                                                            {pi && genMwh > 0 && (
                                                                <div className="text-[10px] text-text-muted">{formatScientific(Math.abs(pi.usd / genMwh))} /MWh</div>
                                                            )}
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </>
                                    )}
                                    {/* Add Project button */}
                                    <button
                                        onClick={() => setAddProjectClientId(client.customer_id)}
                                        className="mt-3 flex items-center gap-2 text-body-sm text-quantum-400 hover:text-quantum-300 transition-colors px-2 py-1"
                                    >
                                        <PlusIcon /> Add Project
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Project create modal scoped to a client */}
            <ProjectCreateModal
                isOpen={!!addProjectClientId}
                onClose={() => setAddProjectClientId(null)}
                customerId={addProjectClientId || ""}
                title="Add Project"
                afterCreate={(projectId, name) => {
                    queryClient.invalidateQueries({ queryKey: ["partner-clients"] });
                    queryClient.invalidateQueries({ queryKey: ["client-dashboard-data", addProjectClientId] });
                    setAddProjectClientId(null);
                    navigate(`/app/projects/${encodeURIComponent(projectId)}?name=${encodeURIComponent(name)}`);
                }}
            />
        </PageLayout>
    );
}

// ---------------------------------------------------------------------------
// Corporate Dashboard — portfolio KPIs with group-by and filtering
// ---------------------------------------------------------------------------

function CorporateDashboard() {
    const { customerName } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [groupBy, setGroupBy] = useState("lifecycle");
    const [showTagManager, setShowTagManager] = useState(false);
    const [analysisMode, setAnalysisMode] = useState<"ctg" | "ops">("ctg");
    const [showReportBuilder, setShowReportBuilder] = useState(false);
    const [projectsCollapsed, setProjectsCollapsed] = useState<boolean | null>(null);

    // Parse filter[key]=value from URL
    const activeFilters = useMemo(() => {
        const filters: Record<string, string> = {};
        searchParams.forEach((value, key) => {
            if (key.startsWith("filter[") && key.endsWith("]")) {
                filters[key.slice(7, -1)] = value;
            }
        });
        return filters;
    }, [searchParams]);

    const hasFilters = Object.keys(activeFilters).length > 0;

    const handleRemoveFilter = (key: string) => {
        const next = new URLSearchParams(searchParams);
        next.delete(`filter[${key}]`);
        setSearchParams(next);
    };

    const handleClearFilters = () => {
        setSearchParams({});
    };

    // When groupBy is "all", pass undefined to backend (returns ungrouped totals)
    const effectiveGroupBy = groupBy === "all" ? undefined : groupBy;

    // ONE consolidated call — returns totals + groups AND both analysis-mode
    // aggregates (ctg + ops) from a single backend DISTINCT ON pull. Previously
    // the dashboard fanned out to /summary + /aggregate (two expensive queries),
    // and toggling Ops<->CtG re-ran /aggregate (~25s) every time. Now the toggle
    // is a client-side selection between overview.ctg / overview.ops.
    const { data: overview, isLoading: overviewLoading, isFetching: overviewFetching } = usePortfolioOverview(
        effectiveGroupBy,
        hasFilters ? activeFilters : undefined,
    );
    const groupedData = overview
        ? { totals: overview.totals, groups: overview.groups, group_by: overview.group_by }
        : undefined;
    const groupedLoading = overviewLoading;
    const totalsData = groupedData; // alias; totals live on the same payload
    const totalsLoading = overviewLoading;
    const { data: tagDefs } = useTagDefinitions();
    // Pick the analysis-mode slice client-side — no re-query on toggle.
    const aggregate = overview ? (analysisMode === "ops" ? overview.ops : overview.ctg) : undefined;
    const aggregateLoading = overviewLoading;
    // Only true while a group-by / filter change refetches — toggling analysis
    // mode is now instant, so this no longer fires on the Ops/CtG switch.
    const aggregateFetching = overviewFetching;
    // Projects for map (shares cache with PortfolioProjectList)
    const { data: mapProjects } = usePortfolioProjects({
        filters: hasFilters ? activeFilters : undefined,
        limit: 200,
    });

    // Format breadcrumb from filters
    const breadcrumb = hasFilters
        ? Object.entries(activeFilters).map(([k, v]) => `${k}: ${v}`).join(" > ")
        : null;

    // Default collapsed state: collapsed when unfiltered (all projects), expanded when filtered
    const isCollapsed = projectsCollapsed ?? !hasFilters;

    return (
        <RangeModeProvider>
        <PageLayout>
            <PageHeader
                title={customerName ? `${customerName} Portfolio` : "Portfolio"}
                subtitle={hasFilters ? undefined : "Portfolio overview and impact aggregation"}
                actions={
                    <div className="flex items-center gap-3">
                        <RangeModeToggle status={overview?.scenarios?.status} />
                        {hasFilters && <ExportButton filters={activeFilters} />}
                        <button
                            onClick={() => setShowReportBuilder(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-body-sm bg-surface-600 text-text-secondary hover:bg-surface-500 hover:text-text-primary transition-colors"
                        >
                            Build report
                        </button>
                        {showReportBuilder && (
                            <PortfolioReportBuilderModal
                                filters={activeFilters}
                                analysisMode={analysisMode}
                                onClose={() => setShowReportBuilder(false)}
                            />
                        )}
                        <Button variant="primary" icon={<TagIcon />} onClick={() => setShowTagManager(true)}>
                            Tag Projects
                        </Button>
                    </div>
                }
            />

            {/* 1. Group-by selector + CTG/Ops toggle — always visible at top */}
            <Section>
                <GroupBySelector
                    selected={groupBy}
                    onChange={setGroupBy}
                    customTags={tagDefs}
                    analysisMode={analysisMode}
                    onAnalysisModeChange={setAnalysisMode}
                />
                {/* Feedback while a group-by / filter change refetches the
                    consolidated payload (keepPreviousData keeps the old cards on
                    screen until the new ones arrive). The Ops/CtG toggle itself
                    is now an instant client-side switch and shows no spinner. */}
                {aggregateFetching && !aggregateLoading && (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-surface-700 border border-border px-3 py-1 text-caption text-text-secondary">
                        <span className="h-2 w-2 rounded-full bg-quantum-400 animate-pulse" />
                        Updating dashboard…
                    </div>
                )}
            </Section>

            {/* 2. Filter bar (only when filters active) */}
            {hasFilters && (
                <Section>
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-body-sm">
                            <button
                                onClick={handleClearFilters}
                                className="text-quantum-400 hover:text-quantum-300 transition-colors"
                            >
                                Portfolio
                            </button>
                            <span className="text-text-muted">&rsaquo;</span>
                            <span className="text-text-primary">{breadcrumb}</span>
                        </div>
                        <PortfolioFilterBar
                            filters={activeFilters}
                            onRemove={handleRemoveFilter}
                            onClear={handleClearFilters}
                        />
                    </div>
                </Section>
            )}

            {/* 3. KPI bar — always visible, uses aggregate for impact values */}
            <PortfolioKPIBar
                totals={aggregate && totalsData?.totals ? {
                    ...totalsData.totals,
                    dalys: aggregate.impacts.Total.DALYs,
                    pdfs: aggregate.impacts.Total.PDFs,
                    usd: aggregate.impacts.Total.USD,
                } : totalsData?.totals}
                isLoading={aggregateLoading && totalsLoading}
                scenarios={overview?.scenarios}
                analysisMode={analysisMode}
            />

            {/* Fleet bracket — only in Range mode when the fleet sweep is complete */}
            <Section>
                <FleetBracket scenarios={overview?.scenarios} analysisMode={analysisMode} />
            </Section>

            {/* 4. Portfolio map (loading skeleton while projects fetch — the
                 prior silent-omit during the ~20s load made it look like the
                 map had disappeared). */}
            {mapProjects === undefined ? (
                <Section>
                    <div
                        className="rounded-xl border border-border bg-surface-700 animate-pulse flex items-center justify-center text-caption text-text-muted"
                        style={{ height: 350 }}
                    >
                        Loading portfolio map…
                    </div>
                </Section>
            ) : mapProjects.length > 0 && mapProjects.some((p) => p.latitude != null) ? (
                <Section>
                    <div className="rounded-xl border border-border overflow-hidden" style={{ height: 350 }}>
                        <PortfolioMap projects={mapProjects} filters={hasFilters ? activeFilters : undefined} />
                    </div>
                </Section>
            ) : null}

            {/* 5. Group cards (when groupBy !== "all" and no drill-down filter) */}
            {groupBy !== "all" && !hasFilters && (
                <Section>
                    {groupedLoading ? (
                        <div className="space-y-3">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="h-16 rounded-xl bg-surface-700 animate-pulse" />
                            ))}
                        </div>
                    ) : groupedData?.groups && groupedData.groups.length > 0 ? (
                        <div className="space-y-3">
                            {groupedData.groups.map((group) => (
                                <PortfolioGroupCard
                                    key={group.group_value || "none"}
                                    group={group}
                                    groupBy={groupBy}
                                />
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            title="No projects yet"
                            description="Create your first project to get started"
                            action={{
                                label: "Create Project",
                                onClick: () => window.location.href = "/app/projects",
                            }}
                        />
                    )}
                </Section>
            )}

            {/* 5. Projects list — always visible, collapsible */}
            <PortfolioProjectList
                filters={hasFilters ? activeFilters : undefined}
                collapsed={isCollapsed}
                onToggle={() => setProjectsCollapsed(!isCollapsed)}
                scenarios={overview?.scenarios}
                analysisMode={analysisMode}
            />

            {/* 6. Viz cards — always visible */}
            <Section>
                {aggregateLoading ? (
                    <div className="space-y-4">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-48 rounded-xl bg-surface-700 animate-pulse" />
                        ))}
                    </div>
                ) : aggregate ? (
                    <div className="space-y-6">
                        <PortfolioImpactExplorer impacts={aggregate.impacts} analysisMode={analysisMode} scenarios={overview?.scenarios} />
                        <PortfolioGenerationChart generation={aggregate.generation} analysisMode={analysisMode} />
                        <PortfolioEmissionsCard emissions={aggregate.emissions} analysisMode={analysisMode} />
                    </div>
                ) : null}
            </Section>

            <TagManager
                isOpen={showTagManager}
                onClose={() => setShowTagManager(false)}
                projectIds={[]}
            />
        </PageLayout>
        </RangeModeProvider>
    );
}

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------

function ImpactPill({ label, value, colorClass }: { label: string; value: string; colorClass: string }) {
    return (
        <div className="rounded-lg bg-surface-600 p-3 text-center">
            <div className={`text-xl font-bold ${colorClass}`}>{value}</div>
            <div className="text-caption text-text-muted mt-1">{label}</div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function PlusIcon() {
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
    );
}

function FolderIcon() {
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
    );
}

function BoltIcon() {
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
    );
}

function AlertIcon({ className }: { className?: string }) {
    return (
        <svg className={`w-5 h-5 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
}

function TagIcon() {
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
    );
}
